// A11 — Job Management list. Same TVP2 §9 styling as AdminUserList:
// bordered Search filter card → bordered action bar + table card,
// Bootstrap palette (Search blue · Add green · Reset/Delete grey).

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatDateHK, formatHKD } from "@findgeeks/shared";
import { apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface JobRow {
  id: string;
  customer_id: string;
  title: string;
  description: string | null;
  status: string;
  price_amount: number | null;
  created_at: string;
  // Embedded by /v1/admin/jobs (relational select on customer_id FK).
  customer: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
}

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "accepted", label: "Accepted" },
  { value: "travelling", label: "Travelling" },
  { value: "on_site", label: "On Site" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export function JobList() {
  const navigate = useNavigate();
  const { show, ToastEl } = useToast();

  const [titleQ, setTitleQ] = useState("");
  const [customerQ, setCustomerQ] = useState("");
  const [statusQ, setStatusQ] = useState<string>("");
  const [filters, setFilters] = useState<{
    q?: string;
    status?: string;
    customer_name?: string;
  }>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const list = usePagedQuery<JobRow>("jobs", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({
      q: titleQ.trim() || undefined,
      customer_name: customerQ.trim() || undefined,
      status: statusQ || undefined,
    });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setTitleQ("");
    setCustomerQ("");
    setStatusQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/jobs/${id}`, { method: "DELETE" });
          results.push({ id, ok: true });
        } catch {
          results.push({ id, ok: false });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Cancelled ${okCount} of ${results.length}`
          : `Cancelled ${okCount} of ${results.length}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const customerLabel = (r: JobRow): string => {
    const c = r.customer;
    if (!c) return "-";
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
    return name || c.display_name || c.email || "-";
  };

  const columns: DataTableColumn<JobRow>[] = [
    {
      field: "customer",
      headerName: "Customer",
      render: customerLabel,
    },
    { field: "title", headerName: "Job Title" },
    {
      field: "description",
      headerName: "Description",
      render: (r) =>
        r.description
          ? r.description.length > 60
            ? r.description.slice(0, 60) + "..."
            : r.description
          : "-",
    },
    {
      field: "price_amount",
      headerName: "Price",
      render: (r) => (r.price_amount != null ? formatHKD(r.price_amount) : "-"),
    },
    { field: "status", headerName: "Status" },
    {
      field: "created_at",
      headerName: "Create Date",
      render: (r) => formatDateHK(r.created_at),
    },
  ];

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search filter card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.colors.admin.tabActive,
              height: 2,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              fontSize: 13,
              color: tokens.colors.admin.textHeading,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            },
          }}
        >
          <Tab
            value="search"
            iconPosition="start"
            icon={<SearchIcon />}
            label="Search"
          />
        </Tabs>

        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Customer Name"
              variant="standard"
              fullWidth
              value={customerQ}
              onChange={(e) => setCustomerQ(e.target.value)}
            />
            <TextField
              label="Job Title"
              variant="standard"
              fullWidth
              value={titleQ}
              onChange={(e) => setTitleQ(e.target.value)}
            />
            <TextField
              label="Status"
              variant="standard"
              select
              fullWidth
              value={statusQ}
              onChange={(e) => setStatusQ(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button type="button" variant="contained" onClick={onReset} sx={greyBtn}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action bar + table card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() => navigate("/app/jobs/add")}
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={selected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>

        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            checkboxSelection
            selectedIds={selected}
            onSelectionChange={setSelected}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => navigate(`/app/jobs/${row.id}`)}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>

      <ConfirmModal
        open={deleteOpen}
        title="Cancel jobs"
        body={`About to cancel ${selected.length} job${selected.length === 1 ? "" : "s"}. This soft-cancels (sets status='cancelled'). Continue?`}
        confirmLabel="Cancel jobs"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(selected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
